import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Tooltip } from '@zoom/react-ui'
import {
  generateTextToImages,
  resetAddImage,
  resetTextToImage,
} from '../vb/actions'
import {
  selectQuery,
  selectIsTryAnotherTextVisible,
  selectRecommendation,
  selectArtisticStyle,
} from './reducer'
import {
  setResultCleared,
  setErrorMessage,
  setIsNotificationVisible,
  updateArtisticStyle,
  updateIsTryAnotherTextVisible,
  updateQuery,
  updateRecommendation,
} from './actions'
import useLightModePreference from '../util/useLightModePreference'
import ErrorMessage from './ErrorMessage'
import { selectTextToImagesLoading } from '../vb/reducer'
import { logGenerateButtonClicked } from '../telemetry/telemetry'
import aiLightModeIcon from './assets/ai-icon.png'
import aiDarkModeIcon from './assets/ai-dark-mode-icon.png'
import clearLightModeIcon from './assets/clear-icon.png'
import clearDarkModeIcon from './assets/clear-dark-mode-icon.png'
import { GENERATE, TRY_AGAIN } from './translation/resources'
import './InputBox.css'

const INPUT_VALUE_MAX_LENGTH = 240

const InputBox = ({ setGeneratedImagesKey, showGeneratedImages }) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch()
  const inputRef = useRef(null)
  const query = useSelector(selectQuery)
  const isTextToImagesLoading = useSelector(selectTextToImagesLoading)
  const isTryAnotherTextVisible = useSelector(selectIsTryAnotherTextVisible)
  const cleanedQuery = !query ? null : query.trim()
  const selectedRecommendation = useSelector(selectRecommendation)
  const selectedArtisticStyle = useSelector(selectArtisticStyle)
  const isLightMode = useLightModePreference()
  const aiIcon = isLightMode ? aiLightModeIcon : aiDarkModeIcon
  const clearIcon = isLightMode ? clearLightModeIcon : clearDarkModeIcon
  const isGeneratedButtonDisabled =
    !cleanedQuery ||
    (!cleanedQuery && !selectedRecommendation) ||
    (isTryAnotherTextVisible && isTextToImagesLoading)

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const handleInputChange = (event) => {
    let { value } = event.target

    // Check if input value length exceeds 180 characters
    if (value.length > INPUT_VALUE_MAX_LENGTH) {
      value = value.slice(0, INPUT_VALUE_MAX_LENGTH)
    }

    dispatch(updateQuery(value))
    dispatch(updateIsTryAnotherTextVisible(false))

    if (!value || value.indexOf(selectedRecommendation) === -1) {
      dispatch(updateRecommendation(null))
    }
  }

  const handleGenerate = (e) => {
    e.preventDefault()
    if (cleanedQuery) {
      const styles = selectedArtisticStyle
        ? ` --style=${selectedArtisticStyle}`
        : ''
      const finalPrompt = cleanedQuery + styles
      inputRef.current?.focus()
      dispatch(resetTextToImage())
      setGeneratedImagesKey((prevKey) => prevKey + 1) // Change the key to trigger re-render
      dispatch(generateTextToImages({ query: finalPrompt }))
      dispatch(updateIsTryAnotherTextVisible(true))
      dispatch(setErrorMessage(null))
      dispatch(setIsNotificationVisible(false))
      dispatch(resetAddImage())
      dispatch(setResultCleared(false))
      logGenerateButtonClicked()
    }
  }

  const handleClear = () => {
    inputRef.current?.focus()
    dispatch(updateQuery(null))
    dispatch(updateArtisticStyle(null))
    dispatch(updateRecommendation(null))
    dispatch(updateIsTryAnotherTextVisible(false))
    dispatch(resetTextToImage())
    dispatch(setResultCleared(true))
    dispatch(setIsNotificationVisible(false))
  }

  const handleContainerClick = () => {
    inputRef.current?.focus()
  }

  return (
    <div className="InputBox__container" onClick={handleContainerClick}>
      <div className="InputBox">
        <img
          draggable={false}
          className="InputBox__aiIcon"
          src={aiIcon}
          alt="ai"
        />
        <form className="InputBox__form" onSubmit={handleGenerate}>
          <input
            tabIndex={0}
            ref={inputRef}
            className={`InputBox__input ${i18n.language === 'ru' ? 'sm' : ''}`}
            onChange={handleInputChange}
            value={query || ''}
            aria-label="Search Virtual Backgrounds"
            placeholder={t('Create virtual backgrounds with AI Companion')}
          />
        </form>

        {query && (
          <Tooltip title={t('Clear text')}>
            <button
              tabIndex={0}
              className="InputBox__clear"
              onClick={handleClear}
              aria-label="Clear search text"
            >
              <img src={clearIcon} alt="clear" />
            </button>
          </Tooltip>
        )}

        <Button
          size="small"
          tabIndex={0}
          className={`InputBox__generate${isGeneratedButtonDisabled ? ' disabled' : ''}`}
          onClick={handleGenerate}
          disabled={isGeneratedButtonDisabled}
        >
          {isTryAnotherTextVisible ? t(TRY_AGAIN) : t(GENERATE)}
        </Button>
      </div>

      <ErrorMessage />
    </div>
  )
}

export default InputBox
